import React, { useState } from 'react';
import { Card } from './ui/card';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ColumnManagerDialog from './ColumnManagerDialog';
import ProductTableHeader from './ProductTableHeader';
import ProductTableRow from './ProductTableRow';
import EmptyState from './EmptyState';
import { useColumnManager } from '../hooks/useColumnManager';
import { useColumnPreferences } from '../contexts/ColumnPreferencesContext';
import { sanitizeText, sanitizeNumber } from '../utils/sanitization';

// Product table with integrated column management
const ProductTableContent = ({
  products = [],
  view = 'quotation',
  onEdit,
  onDelete,
  onPriceChange, // Callback for price changes: (index, newPrice) => void
  onPriceTaxIncludedChange, // Callback for tax-inclusive price changes: (index, newPriceWithTax) => void
  onQuantityChange, // Callback for quantity changes: (index, newQuantity) => void
  onPriceLookup, // Callback for price lookup: (index, product) => void
  onReplace, // Callback for product replacement: (index, currentProduct) => void
  onReorder, // Callback for row reordering: (reorderedProducts) => void
  readOnly = false,
  title = '产品列表',
  taxRate = 0.13, // Default tax rate
  hasAttachments, // Function (product) => boolean or boolean - indicates if product has attachments
  onAttachmentClick, // Callback for attachment icon click: (product, index) => void
  isSample = false, // Indicates if this is a sample order (shows sample icon in sequence column)
  canViewPrices = true // Whether user can view price information
}) => {
  // Single-cell editing state to prevent concurrent editing vulnerability
  const [editingCell, setEditingCell] = useState(null); // { row: number, field: string, value: string }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper function to check if a specific cell is being edited
  const isEditing = (rowIndex, fieldName) => {
    return editingCell?.row === rowIndex && editingCell?.field === fieldName;
  };

  const {

    currentColumns,
    columnManagerOpen,
    openManager,
    closeManager,
    save: saveColumns,
    toggle: toggleColumn,
    reset: resetColumns,
    fieldLabels
  } = useColumnManager(view, {
    onColumnChange: (columns) => {
      console.log(`Column configuration changed for ${view}:`, columns);
    }
  });

  const {
    calculateColumnValue,
    getEnhancedProducts,
    isCalculatedColumn
  } = useColumnPreferences();

  // Enhanced products with calculated columns
  const enhancedProducts = getEnhancedProducts(products, { taxRate });

  // Drag end handler
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id && onReorder) {
      const activeIndex = enhancedProducts.findIndex((item) =>
        (item.line_item_id || item.id || item._id || `item-${enhancedProducts.indexOf(item)}`) === active.id
      );
      const overIndex = enhancedProducts.findIndex((item) =>
        (item.line_item_id || item.id || item._id || `item-${enhancedProducts.indexOf(item)}`) === over.id
      );

      if (activeIndex !== -1 && overIndex !== -1) {
        const reorderedProducts = arrayMove(enhancedProducts, activeIndex, overIndex);
        onReorder(reorderedProducts);
      }
    }
  };

  // Handler for product ID click navigation (opens in new tab)
  const handleProductIdClick = (product) => {
    console.log('handleProductIdClick called with product:', product);
    const productId = product._id || product.id;
    console.log('Extracted productId:', productId);

    if (productId) {
      // Determine referrer based on current page
      const currentPath = window.location.pathname;
      let referrer = 'products';

      if (currentPath.includes('/sales/')) {
        referrer = 'sales';
      } else if (currentPath.includes('/quotations/')) {
        referrer = 'quotations';
      }

      const targetUrl = `/products/${productId}/edit?referrer=${referrer}`;
      console.log('Opening in new tab:', targetUrl);
      // Open product edit page in a new browser tab
      window.open(targetUrl, '_blank');
    } else {
      console.log('No _id found - navigation aborted');
      alert('无法找到产品ID，可能产品已被删除');
    }
  };

  // Unified cell editing handlers
  const handleCellEdit = (rowIndex, fieldName, currentValue) => {
    // Prevent concurrent editing - only allow one cell at a time
    if (editingCell && (editingCell.row !== rowIndex || editingCell.field !== fieldName)) {
      return; // Block if another cell is being edited
    }

    setEditingCell({
      row: rowIndex,
      field: fieldName,
      value: currentValue?.toString() || ''
    });
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCellSave = () => {
    if (!editingCell) return;

    const { row, field, value } = editingCell;

    // Sanitize and validate input based on field type (SECURITY FIX)
    if (field === 'price') {
      const sanitizedPrice = sanitizeNumber(value, { min: 0, max: 999999.99, allowFloat: true });
      if (onPriceChange && sanitizedPrice !== null) {
        onPriceChange(row, sanitizedPrice);
      }
    } else if (field === 'unit_price_tax_included') {
      const sanitizedPriceWithTax = sanitizeNumber(value, { min: 0, max: 999999.99, allowFloat: true });
      if (onPriceTaxIncludedChange && sanitizedPriceWithTax !== null) {
        onPriceTaxIncludedChange(row, sanitizedPriceWithTax);
      }
    } else if (field === 'quantity') {
      const sanitizedQuantity = sanitizeNumber(value, { min: 0, max: 999999, allowFloat: false });
      if (onQuantityChange && sanitizedQuantity !== null) {
        onQuantityChange(row, sanitizedQuantity);
      }
    }

    setEditingCell(null);
  };

  const handleCellKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCellCancel();
    }
  };

  const handleCellValueChange = (newValue) => {
    if (editingCell) {
      // Sanitize input as user types (SECURITY FIX)
      let sanitizedValue = sanitizeText(newValue);

      // Additional validation for numeric fields
      if (editingCell.field === 'price' || editingCell.field === 'unit_price_tax_included' || editingCell.field === 'quantity') {
        // Remove non-numeric characters except decimal point for price
        const allowDecimal = editingCell.field === 'price' || editingCell.field === 'unit_price_tax_included';
        sanitizedValue = sanitizedValue.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');

        // Prevent multiple decimal points
        if (allowDecimal) {
          const parts = sanitizedValue.split('.');
          if (parts.length > 2) {
            sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
          }
        }
      }

      setEditingCell(prev => ({ ...prev, value: sanitizedValue }));
    }
  };



  return (
    <div>
      <ProductTableHeader
        title={title}
        currentColumns={currentColumns}
        fieldLabels={fieldLabels}
        onColumnToggle={toggleColumn}
        onOpenFullManager={openManager}
        onReset={resetColumns}
      />

      {/* Product Table */}
      {enhancedProducts.length > 0 ? (
        readOnly ? (
          /* View-only mode: no drag and drop */
          <Card className="shadow-sm">
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold w-10">#</th>
                    {currentColumns.map(columnId => (
                      <th key={columnId} className="px-2 py-2 text-left font-semibold">
                        {fieldLabels[columnId] || columnId}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {enhancedProducts.map((product, index) => (
                    <ProductTableRow
                      key={product.line_item_id || `${product._id || product.id}-${index}`}
                      product={product}
                      index={index}
                      currentColumns={currentColumns}
                      readOnly={readOnly}
                      editingCell={editingCell}
                      isEditing={isEditing}
                      onCellEdit={handleCellEdit}
                      onCellValueChange={handleCellValueChange}
                      onCellKeyPress={handleCellKeyPress}
                      onCellSave={handleCellSave}
                      onPriceLookup={onPriceLookup}
                      onReplace={onReplace}
                      onDelete={onDelete}
                      onProductIdClick={handleProductIdClick}
                      calculateColumnValue={calculateColumnValue}
                      isCalculatedColumn={isCalculatedColumn}
                      taxRate={taxRate}
                      hasAttachments={
                        typeof hasAttachments === 'function'
                          ? hasAttachments(product)
                          : hasAttachments
                      }
                      onAttachmentClick={onAttachmentClick}
                      isSample={isSample}
                      canViewPrices={canViewPrices}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          /* Edit mode: with drag and drop */
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Card className="shadow-sm">
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold w-10">#</th>
                      {currentColumns.map(columnId => (
                        <th key={columnId} className="px-2 py-2 text-left font-semibold">
                          {fieldLabels[columnId] || columnId}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-center font-semibold w-[100px]">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={enhancedProducts.map((product, index) =>
                      product.line_item_id || product.id || product._id || `item-${index}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody className="divide-y divide-gray-200">
                      {enhancedProducts.map((product, index) => (
                        <ProductTableRow
                          key={product.line_item_id || `${product._id || product.id}-${index}`}
                          product={product}
                          index={index}
                          currentColumns={currentColumns}
                          readOnly={readOnly}
                          editingCell={editingCell}
                          isEditing={isEditing}
                          onCellEdit={handleCellEdit}
                          onCellValueChange={handleCellValueChange}
                          onCellKeyPress={handleCellKeyPress}
                          onCellSave={handleCellSave}
                          onPriceLookup={onPriceLookup}
                          onReplace={onReplace}
                          onDelete={onDelete}
                          onProductIdClick={handleProductIdClick}
                          calculateColumnValue={calculateColumnValue}
                          isCalculatedColumn={isCalculatedColumn}
                          taxRate={taxRate}
                          hasAttachments={
                            typeof hasAttachments === 'function'
                              ? hasAttachments(product)
                              : hasAttachments
                          }
                          onAttachmentClick={onAttachmentClick}
                          isSample={isSample}
                          canViewPrices={canViewPrices}
                    />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </div>
            </Card>
          </DndContext>
        )
      ) : (
        <EmptyState
          message="暂无产品数据"
          subMessage="请添加产品以查看表格"
        />
      )}

      {/* Column Manager Dialog */}
      <ColumnManagerDialog
        open={columnManagerOpen}
        onClose={closeManager}
        currentColumns={currentColumns}
        onSave={saveColumns}
        title={`${title} - 列管理`}
      />
    </div>
  );
};

// Main component (provider is now at App level)
const ProductTable = ProductTableContent;

export default ProductTable;
